'use client'

import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useDocumentation } from '@/hooks/useDocumentation'
import { getContextualHelp } from '@/utils/documentation-parser'
import { DocumentationPanel } from './documentation-panel'
import { DocumentationFAB } from './documentation-fab'
import { createClient } from '@/lib/supabase/client'

interface DocumentationContextType {
  isOpen: boolean
  currentSection: string | null
  currentArticle: string | null
  searchQuery: string
  open: () => void
  close: () => void
  openArticle: (sectionId: string, articleId: string) => void
  openWithContext: () => void
  setSearchQuery: (query: string) => void
  markAsSeen: () => void
  hasSeenDocs: boolean
}

const DocumentationContext = createContext<DocumentationContextType | null>(null)

export function useDocumentationContext() {
  const context = useContext(DocumentationContext)
  if (!context) {
    throw new Error('useDocumentationContext must be used within DocumentationProvider')
  }
  return context
}

interface DocumentationProviderProps {
  children: ReactNode
}

export function DocumentationProvider({ children }: DocumentationProviderProps) {
  const pathname = usePathname()
  const documentation = useDocumentation()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient()
        const { data: { user } } = await supabase.auth.getUser()
        setIsAuthenticated(!!user)
      } catch (error) {
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }
    
    checkAuth()
    
    // Listen for auth changes
    const supabase = createClient()
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user)
    })
    
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Open with context based on current page
  const openWithContext = () => {
    const contextualHelp = getContextualHelp(pathname)
    if (contextualHelp) {
      documentation.openArticle(contextualHelp.sectionId, contextualHelp.articleId)
    } else {
      // Default to getting started
      documentation.openArticle('getting-started', 'login')
    }
  }

  // Keyboard shortcut for opening help (?) - only for authenticated users
  useEffect(() => {
    if (!isAuthenticated) return
    
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check if input is focused
      const target = e.target as HTMLElement
      const isInputFocused = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true'

      if (e.key === '?' && !isInputFocused && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        openWithContext()
      }
      
      if (e.key === 'Escape' && documentation.isOpen) {
        documentation.close()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [documentation.isOpen, pathname, isAuthenticated])

  const contextValue: DocumentationContextType = {
    ...documentation,
    openWithContext
  }

  return (
    <DocumentationContext.Provider value={contextValue}>
      {children}
      {/* Only render documentation components for authenticated users */}
      {!isLoading && isAuthenticated && (
        <>
          <DocumentationPanel />
          <DocumentationFAB />
        </>
      )}
    </DocumentationContext.Provider>
  )
}